import { useEffect, useRef, useState } from 'react';
import { isInviteReturn, signInReturnPath } from '../app-open.js';
import { api } from '../api.js';
import { Wordmark } from './Wordmark.jsx';
import { CoralMark } from './CoralMark.jsx';

// Email first, then the one step that address needs. Nobody has to
// decide between "sign in" and "sign up": the server says whether the
// address has an account (POST /api/auth/lookup) and the screen follows.
//
//   email    -> lookup
//   create   new address: password + terms -> account + session, no email
//            round trip (works even when Resend is having a bad day)
//   password account with a password -> session
//   link     account that has only ever signed in by email link: send one
//   sent     link on its way
export function AuthScreen() {
  const [step, setStep] = useState('email');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [devUrl, setDevUrl] = useState(null);
  // Terms acceptance is required to create an account. Server rejects
  // signup without this; we mirror the same check client-side so the
  // submit button stays disabled until the box is checked.
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [err, setErr] = useState('');
  const passwordRef = useRef(null);
  const emailRef = useRef(null);

  const normalized = email.trim().toLowerCase();

  useEffect(() => {
    if (step === 'password' || step === 'create') passwordRef.current?.focus();
    if (step === 'email') emailRef.current?.focus();
  }, [step]);

  const go = (next) => {
    setErr('');
    setPassword('');
    setShowPassword(false);
    setStep(next);
  };

  // A tagged link (/app?src=rap26) rides the sign-in request itself, so the
  // account is tagged even where no source cookie may be set without
  // consent (EU/UK).
  const withSrc = (body) => {
    const src = new URLSearchParams(window.location.search).get('src');
    return src ? { ...body, src } : body;
  };

  const submitEmail = async () => {
    if (!normalized) return;
    setBusy(true);
    setErr('');
    try {
      const r = await api('/api/auth/lookup', {
        method: 'POST',
        body: withSrc({ email: normalized }),
      });
      go(!r.exists ? 'create' : r.has_password ? 'password' : 'link');
    } catch (e) {
      setErr(e.message || 'Something went wrong. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const submitPassword = async () => {
    if (!password) return;
    setBusy(true);
    setErr('');
    try {
      await api('/api/auth/password', {
        method: 'POST',
        body: { email: normalized, password },
      });
      window.location.reload();
    } catch (e) {
      setBusy(false);
      setErr(
        e.payload?.fallback_magic && !/too many/i.test(e.message || '')
          ? "That password doesn't match. Try again, or get a sign-in link by email."
          : e.message || 'Sign in failed.'
      );
    }
  };

  const submitCreate = async () => {
    if (password.length < 8) return;
    if (!acceptedTerms) {
      setErr('Please accept the Terms of Use and Privacy Policy to create an account.');
      return;
    }
    setBusy(true);
    setErr('');
    try {
      await api('/api/auth/signup', {
        method: 'POST',
        body: withSrc({ email: normalized, password, accepted_terms: true }),
      });
      window.location.reload();
    } catch (e) {
      setBusy(false);
      // The address gained an account between the lookup and now.
      if (e.payload?.fallback_signin) return go('password');
      if (e.payload?.fallback_magic) return go('link');
      setErr(e.message || 'Could not create account.');
    }
  };

  const sendLink = async () => {
    if (!normalized) return;
    setBusy(true);
    setErr('');
    setDevUrl(null);
    try {
      const next = signInReturnPath(window.location.pathname, window.location.search);
      const r = await api('/api/auth/request', {
        method: 'POST',
        body: withSrc(next ? { email: normalized, next } : { email: normalized }),
      });
      if (r.devUrl) setDevUrl(r.devUrl);
      else if (r.sent === false) {
        // Saying "check your email" for a link that never left strands the
        // keeper waiting on nothing.
        setErr("We couldn't send the email just now. Try again in a minute.");
        return;
      }
      setStep('sent');
    } catch (e) {
      setErr(e.message || 'Something went wrong. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (busy) return;
    if (step === 'email') submitEmail();
    else if (step === 'password') submitPassword();
    else if (step === 'create') submitCreate();
    else if (step === 'link') sendLink();
  };

  const inputBase = {
    width: '100%',
    background: 'transparent',
    color: 'var(--text)',
    border: 'none',
    borderBottom: '1px solid var(--surface-border)',
    padding: '10px 0',
    fontSize: 18,
    outline: 'none',
    transition: 'border-color 0.2s',
  };
  const labelStyle = {
    display: 'block',
    fontSize: 11,
    letterSpacing: '0.22em',
    textTransform: 'uppercase',
    color: 'var(--text-dim)',
    marginBottom: 6,
  };
  const linkButton = { color: 'var(--cyan)', textDecoration: 'underline', padding: 0 };
  const quietButton = {
    marginTop: 12,
    width: '100%',
    padding: '8px 0',
    fontSize: 12,
    letterSpacing: '0.2em',
    textTransform: 'uppercase',
    color: 'var(--text-muted)',
    borderRadius: 10,
  };

  const copy = {
    email: {
      eyebrow: 'Welcome',
      heading: 'Sign in or create your account.',
      helper: "Enter your email. We'll take it from there.",
      action: 'Continue',
      busy: 'Checking…',
    },
    password: {
      eyebrow: 'Sign in to your tank',
      heading: 'Welcome back, reefer.',
      helper: null,
      action: 'Sign in',
      busy: 'Signing in…',
    },
    create: {
      eyebrow: 'Create your account',
      heading: 'Start tracking your reef.',
      helper: "Pick a password and you're in. No email to confirm.",
      action: 'Create account',
      busy: 'Creating account…',
    },
    link: {
      eyebrow: 'Sign in to your tank',
      heading: 'Welcome back, reefer.',
      helper:
        "This account signs in with an email link. We'll send one now. Once you're in, you can set a password in Account so next time is one step.",
      action: 'Email me a sign-in link',
      busy: 'Sending link…',
    },
  }[step] || { action: '', busy: '' };

  const invite = typeof window !== 'undefined' && isInviteReturn(window.location.search);
  if (invite && step === 'email') {
    copy.eyebrow = 'Tank invite';
    copy.heading = 'Someone shared their tank with you.';
    copy.helper =
      'Use the email address the invite was sent to. New to TankSensei? You can make an account here, free during beta.';
  }

  const fromRing =
    typeof window !== 'undefined' && /^\/app\/connect\/ring\/?$/i.test(window.location.pathname);
  if (fromRing && step === 'link') {
    copy.helper =
      "This account signs in with an email link. Ring's link to finish lasts 10 minutes, so open the email soon.";
  } else if (fromRing && step === 'email') {
    copy.helper =
      'Sign in to finish linking your Ring cameras. Ring’s link lasts 10 minutes. Ring cameras are a small test, open to the keepers we have added to it.';
  }

  const disabled =
    busy ||
    !normalized ||
    (step === 'password' && !password) ||
    (step === 'create' && (password.length < 8 || !acceptedTerms));

  const emailChip = (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 10,
        padding: '0 4px 0 14px',
        minHeight: 44,
        borderRadius: 12,
        background: 'var(--surface-bg)',
        border: '1px solid var(--surface-border)',
        marginBottom: 18,
      }}
    >
      <span
        style={{
          fontSize: 16,
          color: 'var(--text)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {normalized}
      </span>
      <button
        type="button"
        onClick={() => go('email')}
        className="font-heading"
        style={{
          fontSize: 12,
          letterSpacing: '0.18em',
          textTransform: 'uppercase',
          color: 'var(--cyan)',
          padding: '0 4px',
          minHeight: 44,
          minWidth: 44,
          flexShrink: 0,
        }}
      >
        Change
      </button>
    </div>
  );

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 20px',
      }}
    >
      <div className="fade-in" style={{ width: '100%', maxWidth: 440 }}>
        <div style={{ textAlign: 'center', marginBottom: 36 }}>
          <Wordmark size={36} />
        </div>
        <div
          style={{
            background: 'var(--surface)',
            border: '1px solid var(--surface-border)',
            borderRadius: 20,
            padding: 28,
          }}
        >
          {step === 'sent' ? (
            <div style={{ textAlign: 'center', padding: '10px 0' }}>
              <CoralMark size={44} style={{ marginBottom: 2 }} />
              <div
                className="font-heading"
                style={{
                  fontSize: 11,
                  letterSpacing: '0.3em',
                  color: 'var(--text-dim)',
                  marginBottom: 12,
                }}
              >
                SENSEI
              </div>
              <h2 className="font-heading" style={{ fontSize: 20, marginBottom: 8 }}>
                Check your email.
              </h2>
              <p
                style={{
                  fontSize: 15,
                  lineHeight: 1.55,
                  color: 'var(--text-muted)',
                  marginBottom: 16,
                }}
              >
                We sent a sign-in link to{' '}
                <strong style={{ color: 'var(--text)', fontWeight: 500 }}>{normalized}</strong>. It
                expires in 15 minutes.
              </p>
              <p
                style={{
                  fontSize: 13,
                  lineHeight: 1.55,
                  color: 'var(--text-dim)',
                  marginBottom: 16,
                }}
              >
                Not arriving? Check spam, or{' '}
                <button type="button" onClick={sendLink} disabled={busy} style={linkButton}>
                  send it again
                </button>
                .
              </p>
              {err && (
                <p role="alert" style={{ fontSize: 14, color: 'var(--alert)', marginBottom: 10 }}>
                  {err}
                </p>
              )}
              {devUrl && (
                <div
                  style={{
                    marginTop: 12,
                    padding: 12,
                    borderRadius: 10,
                    background: 'var(--accent-bg)',
                    border: '1px solid var(--accent-border)',
                    textAlign: 'left',
                  }}
                >
                  <div
                    className="font-heading"
                    style={{
                      fontSize: 11,
                      letterSpacing: '0.2em',
                      textTransform: 'uppercase',
                      color: 'var(--cyan)',
                      marginBottom: 6,
                    }}
                  >
                    Dev mode · no email sent
                  </div>
                  <a
                    href={devUrl}
                    className="font-mono"
                    style={{
                      fontSize: 13,
                      color: 'var(--text)',
                      wordBreak: 'break-all',
                      textDecoration: 'underline',
                    }}
                  >
                    {devUrl}
                  </a>
                </div>
              )}
              <button
                onClick={() => go('email')}
                className="font-heading"
                style={{
                  marginTop: 18,
                  fontSize: 12,
                  letterSpacing: '0.2em',
                  textTransform: 'uppercase',
                  padding: '8px 16px',
                  borderRadius: 999,
                  color: 'var(--text-muted)',
                  border: '1px solid var(--surface-border)',
                }}
              >
                Back
              </button>
            </div>
          ) : (
            <form onSubmit={onSubmit} noValidate>
              <div
                className="font-heading"
                style={{
                  fontSize: 12,
                  letterSpacing: '0.28em',
                  textTransform: 'uppercase',
                  color: 'var(--cyan)',
                  marginBottom: 6,
                }}
              >
                {copy.eyebrow}
              </div>
              <h1
                className="font-heading"
                style={{ fontSize: 24, lineHeight: 1.15, marginBottom: 10, fontWeight: 600 }}
              >
                {copy.heading}
              </h1>
              {copy.helper && (
                <p
                  style={{
                    fontSize: 15,
                    lineHeight: 1.55,
                    color: 'var(--text-muted)',
                    marginBottom: 22,
                  }}
                >
                  {copy.helper}
                </p>
              )}

              {step === 'email' ? (
                <>
                  <label htmlFor="email" className="font-heading" style={labelStyle}>
                    Email
                  </label>
                  <input
                    id="email"
                    ref={emailRef}
                    type="email"
                    inputMode="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="username"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    required
                    autoFocus
                    placeholder="you@example.com"
                    style={inputBase}
                  />
                </>
              ) : (
                <>
                  {emailChip}
                  {/* Kept in the form so password managers pair the saved
                      password with this address. */}
                  <input
                    type="email"
                    name="username"
                    autoComplete="username"
                    value={normalized}
                    readOnly
                    tabIndex={-1}
                    aria-hidden="true"
                    style={{ position: 'absolute', width: 1, height: 1, opacity: 0 }}
                  />
                </>
              )}

              {(step === 'password' || step === 'create') && (
                <>
                  <label htmlFor="password" className="font-heading" style={labelStyle}>
                    {step === 'create' ? 'Choose a password · 8+ characters' : 'Password'}
                  </label>
                  <div style={{ position: 'relative' }}>
                    <input
                      id="password"
                      ref={passwordRef}
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete={step === 'create' ? 'new-password' : 'current-password'}
                      required
                      minLength={step === 'create' ? 8 : 1}
                      placeholder={step === 'create' ? 'Pick a strong one' : 'Your password'}
                      style={{ ...inputBase, paddingRight: 56 }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      className="font-heading"
                      style={{
                        position: 'absolute',
                        right: 0,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        fontSize: 11,
                        letterSpacing: '0.18em',
                        textTransform: 'uppercase',
                        color: 'var(--text-dim)',
                        padding: '0 8px',
                        minHeight: 44,
                        minWidth: 44,
                      }}
                    >
                      {showPassword ? 'Hide' : 'Show'}
                    </button>
                  </div>
                </>
              )}

              {err && (
                <p role="alert" style={{ fontSize: 14, color: 'var(--alert)', marginTop: 10 }}>
                  {err}
                </p>
              )}

              {step === 'create' && (
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 10,
                    margin: '20px 0 0',
                    fontSize: 15,
                    color: 'var(--text-muted)',
                    cursor: 'pointer',
                    lineHeight: 1.5,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={acceptedTerms}
                    onChange={(e) => setAcceptedTerms(e.target.checked)}
                    style={{
                      width: 24,
                      height: 24,
                      margin: 0,
                      accentColor: 'var(--cyan)',
                      flexShrink: 0,
                    }}
                  />
                  <span>
                    I'm 18 or older, and I've read and agree to the{' '}
                    <a
                      href="/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: 'var(--cyan)' }}
                    >
                      Terms of Use
                    </a>{' '}
                    and{' '}
                    <a
                      href="/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: 'var(--cyan)' }}
                    >
                      Privacy Policy
                    </a>
                    . Sensei is an AI helper, not a chemist, alerts are not a safety system, and I'm
                    the one in charge of my tank.
                  </span>
                </label>
              )}

              <button
                type="submit"
                disabled={disabled}
                className="font-heading"
                style={{
                  marginTop: 22,
                  width: '100%',
                  padding: '14px 0',
                  borderRadius: 14,
                  background: 'var(--cyan)',
                  color: 'var(--cyan-text)',
                  fontWeight: 600,
                  fontSize: 14,
                  letterSpacing: '0.18em',
                  textTransform: 'uppercase',
                  opacity: disabled ? 0.5 : 1,
                  cursor: busy ? 'not-allowed' : 'pointer',
                }}
              >
                {busy ? copy.busy : copy.action}
              </button>

              {step === 'password' && (
                <button
                  type="button"
                  onClick={sendLink}
                  disabled={busy}
                  className="font-heading"
                  style={quietButton}
                >
                  Forgot password? Email me a sign-in link
                </button>
              )}
            </form>
          )}
        </div>
        <p style={{ fontSize: 13, color: 'var(--text-dim)', textAlign: 'center', marginTop: 20 }}>
          <a
            href="/"
            style={{
              color: 'var(--cyan)',
              display: 'inline-flex',
              alignItems: 'center',
              minHeight: 44,
              padding: '0 8px',
            }}
          >
            Learn about TankSensei
          </a>
        </p>
      </div>
    </div>
  );
}
